import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { toPromise } from '@greco-fit/util';
import {
  AdditionalSpotDetails,
  BookingOptionDetails,
  EventAccount,
  EventWithUserDetails,
  SpotDetails,
} from '@greco/booking-events2';
import { UserCommunityAgreement } from '@greco/community-agreements';
import { User } from '@greco/identity-users';
import { AlertConfig, AlertType } from '@greco/ngx-alerts';
import { SignAgreementDialog } from '@greco/ngx-community-agreements';
import { PropertyListener } from '@greco/property-listener-util';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { EventService } from '../../services/event.service';

@Component({
  selector: 'alt-booking-card2',
  templateUrl: './booking-card.component.html',
  styleUrls: ['./booking-card.component.scss'],
})
export class BookingCard2Component {
  constructor(
    private dialog: MatDialog,
    public eventSvc: EventService,
    public bottomSheet: MatBottomSheet,
    private breakpointObserver: BreakpointObserver
  ) {}

  @ViewChild('bottomSheetContent', { static: false }) bottomSheetContent!: TemplateRef<void>;

  @PropertyListener('event') event$ = new BehaviorSubject<EventWithUserDetails | null>(null);
  @Input() event!: EventWithUserDetails;

  @PropertyListener('user') user$ = new BehaviorSubject<User | null>(null);
  @Input() user!: User;

  @PropertyListener('spotId') spotId$ = new BehaviorSubject<string | null>(null);
  @Input() spotId?: string;

  @PropertyListener('waitlist') waitlist$ = new BehaviorSubject<User[]>([]);
  @Input() waitlist: User[] = [];

  @PropertyListener('isStaffView') isStaffView$ = new BehaviorSubject<boolean>(true);
  @Input() isStaffView = true;

  @PropertyListener('canLeaveUnsigned') canLeaveUnsigned$ = new BehaviorSubject<boolean>(false);
  @Input() canLeaveUnsigned = true;

  @PropertyListener('canOverrideAdditionalSpots') canOverrideAdditionalSpots$ = new BehaviorSubject<boolean>(false);
  @Input() canOverrideAdditionalSpots = false;

  @Input() readonly = false;
  @Input() bookingCount = 1;
  @Input() selectedBookingOption?: BookingOptionDetails;
  @Input() eventAccounts: EventAccount[] = [];
  @Input() altAccounts: EventAccount[] = [];
  @Input() confirming = false;
  @Input() boostersActivated = false;

  @Input() allowRemoval = true;

  hasBookingAgreements = false;
  allBookingAgreementsSigned = false;

  selectedSpot: SpotDetails | null = null;

  everyoneText = '';

  $bottomSheetRef?: Subscription;

  isMobile$ = this.breakpointObserver.observe('(max-width: 600px)').pipe(map(bps => bps.matches));

  spot$ = combineLatest([this.event$, this.spotId$]).pipe(
    map(([event, spotId]) => {
      if (!event || !spotId) return null;

      return event.requirements?.spotBooking?.room?.spots?.find(s => s.id === spotId);
    })
  );

  selectedEquipment$ = this.eventSvc.equipment$.pipe(
    map(equipment => equipment.find(equipment => equipment.userId === this.user.id)?.equipment)
  );

  isWaitlisted$ = combineLatest([this.user$, this.waitlist$]).pipe(
    map(([user, waitlist]) => waitlist.some(w => w.id === user?.id))
  );

  pageData$ = combineLatest([
    this.user$,
    this.event$,
    this.spotId$,
    this.isStaffView$,
    this.canLeaveUnsigned$,
    this.eventSvc.additionalSpots$,
  ]).pipe(
    map(([user, event, spotId, isStaffView, canLeaveUnsigned, additionalSpots]) => ({
      user,
      bookedBy: event?.accounts[0],
      event,
      spotId,
      isStaffView,
      canLeaveUnsigned,
      additionalSpots,
    })),
    tap(
      data =>
        (this.everyoneText =
          `A purchase is required to book this event. Drop-in perks can be used if you don't have any available perks to book this event.${
            data.additionalSpots?.length
              ? ' Any additional spots reserved count towards the total capacity of this event. '
              : ''
          } Confirm your booking to complete your purchase or <a href="mailto:` +
          data?.event?.communityEmail +
          '">contact us</a> for help.')
    )
  );

  unsignedAgreementAlerts$ = combineLatest([
    this.user$,
    this.event$,
    this.eventSvc.agreementSubmissions$,
    this.eventSvc.bookingOptionAgreements$,
  ]).pipe(
    map(([user, event, agreementSubmissions, bookingOptionAgreements]) => {
      if (!user) return;

      const alerts: (AlertConfig & { agreementId: string })[] = [];

      agreementSubmissions.forEach(submission => {
        if (submission.userId !== user.id) return;

        if (submission.unsigned && !submission.signed) {
          const agreement =
            event?.requirements.agreements?.find(agreement => agreement.agreementId === submission.agreementId) ??
            bookingOptionAgreements.find(agreement => agreement.agreementId === submission.agreementId);

          if (submission.userId === event?.accounts[0].user.id) {
            alerts.push({
              agreementId: submission.agreementId,
              title: (agreement?.agreement?.title || 'Agreement') + ' is Unsigned',
              type: AlertType.DANGER,
              description: 'Please make sure all agreements are signed before continuing',
            });
          } else {
            alerts.push({
              agreementId: submission.agreementId,
              title: (agreement?.agreement?.title || 'Agreement') + ' is Unsigned',
              type: AlertType.WARN,
              description:
                'Please make sure ' +
                event?.accounts.find(account => account.user.id === submission.userId)?.user.displayName +
                ' signs all their agreements before continuing',
            });
          }
        }
      });

      return alerts;
    })
  );

  bookingErrors$ = combineLatest([
    this.pageData$,
    this.eventSvc.agreementSubmissions$,
    this.eventSvc.typeformSubmissions$,
    this.eventSvc.profilesCompleted$,
  ]).pipe(
    map(([pageData, agreementSubmissions, typeformSubmissions, profilesCompleted]) => {
      const { user, bookedBy, event, spotId, isStaffView, canLeaveUnsigned } = pageData;

      let warning = false;
      let error = false;

      if (!user || !event) {
        return {
          warning,
          error,
        };
      }

      // Profile Completion
      if (
        user.id === event.accounts[0].user.id &&
        !user.address?.line1 &&
        !profilesCompleted.some(profile => profile.userId === user.id && profile.completed)
      ) {
        if (isStaffView) {
          warning = true;
        } else {
          error = true;
        }
      } else if (
        user.id !== event.accounts[0].user.id &&
        !user.address?.line1 &&
        !profilesCompleted.some(profile => profile.userId === user.id && profile.completed)
      ) {
        warning = true;
      }

      // Agreements
      if (agreementSubmissions?.length) {
        const hasAgreementsToSign = agreementSubmissions.filter(submission => submission.userId === user.id).length > 0;
        const agreementsToSign = agreementSubmissions.filter(submission => submission.userId === user.id);

        if (hasAgreementsToSign || !agreementsToSign.every(submission => submission.signed)) {
          canLeaveUnsigned || (user.id !== bookedBy?.user?.id && user.contactEmail !== bookedBy?.user?.contactEmail)
            ? (warning = true)
            : (error = true);
        }

        if (agreementsToSign.some(submission => submission.fromBooking)) {
          this.hasBookingAgreements = true;

          if (agreementsToSign.every(submission => submission.signed)) {
            this.allBookingAgreementsSigned = true;
          } else this.allBookingAgreementsSigned = false;
        }
      }

      // Spot Booking
      if (event.requirements.spotBooking?.spotBookingEnabled && event.requirements.spotBooking.room && !spotId) {
        error = true;
      }

      // Typeform
      if (
        event.requirements.forms?.length &&
        !event?.requirements?.forms?.every(
          form =>
            !form.required ||
            typeformSubmissions.some(submission => submission.formId === form.id && submission.userId === user.id)
        )
      ) {
        error = true;
      } else if (
        event.requirements.forms?.length &&
        event?.requirements?.forms?.some(
          form =>
            !form.required &&
            !typeformSubmissions.some(submission => submission.formId === form.id && submission.userId === user.id)
        )
      ) {
        warning = true;
      }

      return {
        warning,
        error,
      };
    })
  );

  addAdditionalSpot() {
    if (!this.user || !this.selectedBookingOption || !this.selectedBookingOption.additionalSpots) return;

    const additionalSpotCount = this.eventSvc.additionalSpots$.value.length || 0;
    if (
      (additionalSpotCount >= this.selectedBookingOption.additionalSpots && !this.canOverrideAdditionalSpots) ||
      additionalSpotCount + 1 >= this.event.capacityRemaining
    ) {
      this.eventSvc.additionalSpots$.next([
        ...this.eventSvc.additionalSpots$.value.filter(spot => spot.userId !== this.user.id),
      ]);
    } else {
      const additionalSpots = this.eventSvc.additionalSpots$.value.filter(spot => spot.userId !== this.user.id);
      let userAdditionalSpots = this.eventSvc.additionalSpots$.value.filter(spot => spot.userId === this.user.id);

      const userAdditionalSpot: AdditionalSpotDetails = {
        userId: this.user.id,
        bookingOptionId: this.selectedBookingOption.id,
        cost: this.selectedBookingOption?.additionalSpotCost || 0,
      };

      if (!userAdditionalSpots?.length) userAdditionalSpots = [userAdditionalSpot];
      else userAdditionalSpots.push(userAdditionalSpot);

      this.eventSvc.additionalSpots$.next([...additionalSpots, ...userAdditionalSpots]);
    }
  }

  removeAdditionalSpot() {
    if (!this.user || !this.selectedBookingOption || !this.selectedBookingOption.additionalSpots) return;

    const value = this.eventSvc.additionalSpots$.value;
    if (value.length <= 1) this.eventSvc.additionalSpots$.next([]);
    else {
      const index = value.findIndex(
        spot => spot.userId === this.user.id && spot.bookingOptionId === this.selectedBookingOption?.id
      );
      if (index === -1) return;

      this.eventSvc.additionalSpots$.next(value.splice(index, 1));
    }
  }

  selectEquipment(equipment: string) {
    const otherEquipment = this.eventSvc.equipment$.value.filter(equipment => equipment.userId !== this.user.id);
    this.eventSvc.equipment$.next([...otherEquipment, { userId: this.user.id, equipment }]);
  }

  swapBooking(next: EventAccount) {
    const accountWithBookingOptions = next.canUsePerks && next.bookingOptions.length ? next : this.event.accounts[0];
    let bookingOptions = accountWithBookingOptions.bookingOptions;

    if (accountWithBookingOptions.user.id !== next.user.id) {
      bookingOptions = bookingOptions.filter(option => option.transferable || option.transferableReusable);
    }

    this.eventSvc.swapBooking(this.user.id, {
      userId: next.user.id,
      eventId: this.event.id,
      bookingOptionId: bookingOptions[0]?.id || '',
      bookingOptionUserId: bookingOptions[0]?.userId || '',
    });
  }

  removeBooking() {
    this.eventSvc.removeBooking(this.user.id);
  }

  openRequirementRef(ref: TemplateRef<any>) {
    this.bottomSheet.dismiss();

    const bottomSheetRef = this.bottomSheet.open(ref, { panelClass: 'bottom-sheet' });

    this.$bottomSheetRef = bottomSheetRef.afterDismissed().subscribe(() => {
      this.bottomSheet.open(this.bottomSheetContent, { panelClass: 'bottom-sheet' });
      this.$bottomSheetRef?.unsubscribe();
    });
  }

  async signAgreement(agreement: UserCommunityAgreement) {
    const result = await toPromise(this.dialog.open(SignAgreementDialog, { data: agreement }).afterClosed());
    if (result) {
      const unsignedAgreements = this.eventSvc.unsignedAgreements$.value;
      this.eventSvc.unsignedAgreements$.next(
        unsignedAgreements.filter(unsignedAgreement => unsignedAgreement.id !== agreement.id)
      );
    }
  }

  updateSelectedSpot(spot: SpotDetails) {
    this.selectedSpot = spot;
  }

  async updateSpot() {
    if (!this.selectedSpot) return;

    const bookings = this.eventSvc.bookings$.value;
    const userBooking = bookings.find(booking => booking.userId === this.user?.id);
    if (!userBooking) return;

    userBooking.spotId = this.selectedSpot.spotId;

    const bookingIndex = bookings.findIndex(booking => booking.userId === this.user?.id);

    bookings[bookingIndex] = userBooking;
    this.eventSvc.bookings$.next([...bookings]);

    this.selectedSpot = null;
  }

  async leaveWaitlist() {
    try {
      await this.eventSvc.removeFromWaitlist(this.event.location.id, this.event.id, this.user.id);
    } catch (err) {
      console.error(err);
    }
  }
}
